/* eslint-disable extra-rules/no-commented-out-code */
/* eslint-disable complexity */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useEffect, useCallback, useState } from 'react';
import Router from 'next/router';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import { setLocalStorage, getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import { scrollToBreadCrumb, isMobileWeb } from '@tcp/core/src/utils';
import { getSessionStorage } from '@tcp/core/src/utils/utils.web';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import internalEndpoints from '@tcp/core/src/components/features/account/common/internalEndpoints';
import { getLabelFromCopyOrFromTranslation } from '@tcp/core/src/utils/labels/labelUtilty';
import {
  GlobalAccessibilityLabels,
  GLOBAL_ACCESSIBILITY_LABELS,
  condensedNavigationAriaLabel,
} from '@tcp/core/src/utils/labels/globalAccessibility.labels';
import { headerLabels } from '@tcp/core/src/utils/labels/globalHeader.labels';
import HelpMenuLabels from '@tcp/core/src/components/common/organisms/HelpMenu/container/HelpMenu.label';
import Drawer from '../molecules/Drawer';
import NavBar from '../organisms/NavBar';
import style, { StoreAndHelpLinkWrapper } from '../Navigation.style';
import { FindAStoreIcon, HelpIcon } from './NavigationIcons';

const getCustomerHelp = (url) => {
  return url?.match(/\/customer-help\//g);
};

const getIsListingPage = (url) => {
  return url?.match(/\/category\//g) || url?.match(/\/c\//g) || url?.match(/\/search\//g);
};

const getIsScrollTop = (
  isListingPage,
  isUniqueUrl,
  isCustomerHelp,
  backFromPDP,
  fromSBCCaraosal
) => {
  return (!isListingPage || (isUniqueUrl && !backFromPDP)) && !isCustomerHelp && !fromSBCCaraosal;
};

const Footer = dynamic(() =>
  // eslint-disable-next-line extra-rules/no-commented-out-code
  import(/* webpackChunkName: "footer-chunk" */ '../../Footer/container/Footer.container')
);

/**
 * This function scrolls page to top on route change complete
 */
const routeCompleteHandler = (url, isPlpPdpAnchoringEnabled, isDisableBagAnchoring) => {
  const isListingPage = getIsListingPage(url);
  const isCustomerHelp = getCustomerHelp(url);
  const fromSBCCaraosal = url?.includes('viaModule=promoFilter');
  const isPDP = url?.match(/\/p\//g);
  const isPDPPLPSearch = isListingPage || isPDP;
  const isBagPage = url?.match(/\/bag$/g);
  const previousUrl = getLocalStorage('previousUrl') || '';
  const [asPath] = url?.split('?') || [];
  const [previousAsPath] = previousUrl.split('?') || [];
  const isUniqueUrl = previousAsPath && previousAsPath.split('/')[3] !== asPath.split('/')[3];
  const isScrollEnabled = isPDP;
  const fromPDP = previousUrl && previousUrl.includes('/p/');
  const backFromPDP =
    isListingPage && getSessionStorage('LAST_PAGE_PATH') === window.location.pathname && fromPDP;
  if (
    isMobileWeb() &&
    ((isPlpPdpAnchoringEnabled && isPDPPLPSearch && isUniqueUrl && !backFromPDP) ||
      (!isDisableBagAnchoring && isBagPage) ||
      (isPDP && !isUniqueUrl))
  ) {
    scrollToBreadCrumb(isBagPage, isScrollEnabled);
  } else if (
    getIsScrollTop(isListingPage, isUniqueUrl, isCustomerHelp, backFromPDP, fromSBCCaraosal)
  ) {
    // If not listing page or if listing page with different URL and not backfrompdp to same page, scroll to top
    window.scrollTo(0, 0);
  }
  setLocalStorage({ key: 'previousUrl', value: url });
};

const Navigation = (props) => {
  const {
    nav: navigationData = [],
    openNavigationDrawer,
    className,
    userName,
    userPoints,
    userRewards,
    userNameClick,
    onLinkClick,
    triggerLoginCreateAccount,
    closeNavigationDrawer,
    hideNavigationFooter,
    showCondensedHeader,
    openOverlay,
    isCondensedHeader,
    onRouteChangeGlobalActions,
    isPlpPdpAnchoringEnabled,
    isDisableBagAnchoring,
    isShowNavAnimation,
    isNewNavigationImagesEnabled,
    // brandId,
    isOutfitPLPEnabled,
    isHamburgerMenuRedesignEnabled,
    isGuestFavEnabled,
    isGlobalHeaderRedesignEnabled,
    isMobileNavigationRedesignEnabled,
    hideDrawerInfoSection,
    // setHideDrawerInfoSection,
    isGlobalDefaultHeaderRedesignEnabled,
    onStorePinClicked,
    isBrandPartnerHoverEffectEnabled,
  } = props;
  const condensedNavigationAriaLbl = getLabelFromCopyOrFromTranslation(
    `${GLOBAL_ACCESSIBILITY_LABELS}.${condensedNavigationAriaLabel}`,
    GlobalAccessibilityLabels
  );

  const [selectedL1Index, setSelectedL1Index] = useState(-1);

  const visibleItemCount = navigationData.filter(
    (item) => item?.categoryContent?.displayToCustomer
  ).length;

  const navBarProps = {
    ...props,
    setSelectedL1Index,
    visibleItemCount,
  };

  const handleRouteChange = useCallback(() => {
    setTimeout(() => {
      onRouteChangeGlobalActions();
      closeNavigationDrawer();
    }, 0);
  }, [onRouteChangeGlobalActions, closeNavigationDrawer]);

  const handleRouteComplete = useCallback(
    (url) => {
      routeCompleteHandler(url, isPlpPdpAnchoringEnabled, isDisableBagAnchoring);
    },
    [isPlpPdpAnchoringEnabled, isDisableBagAnchoring]
  );

  useEffect(() => {
    Router.events.on('routeChangeStart', handleRouteChange);
    // Router.events.on('routeChangeComplete', handleRouteComplete);

    return () => {
      Router.events.off('routeChangeStart', handleRouteChange);
      // Router.events.off('routeChangeComplete', handleRouteComplete);
    };
  }, [handleRouteChange, handleRouteComplete]);

  const drawerWidthMobileAndTablet = isMobileNavigationRedesignEnabled ? '343px' : '314px';

  return (
    <Drawer
      id="l1_drawer"
      small
      medium
      selectedL1Index={selectedL1Index}
      visibleItemCount={visibleItemCount}
      userName={userName}
      userPoints={userPoints}
      userRewards={userRewards}
      userNameClick={userNameClick}
      onLinkClick={onLinkClick}
      triggerLoginCreateAccount={triggerLoginCreateAccount}
      open={openNavigationDrawer}
      close={closeNavigationDrawer}
      openOverlay={openOverlay}
      width={{
        small: drawerWidthMobileAndTablet,
        medium: drawerWidthMobileAndTablet,
        large: '100%',
      }}
      position={{
        top: !showCondensedHeader ? '155px' : '316px',
        left: 0,
        topMedium: '111px',
      }}
      height="100%"
      renderOverlay
      drawerFooter={Footer}
      hideNavigationFooter={hideNavigationFooter}
      showCondensedHeader={showCondensedHeader}
      navigationLevel="l1"
      isShowNavAnimation={isShowNavAnimation}
      isNewNavigationImagesEnabled={isNewNavigationImagesEnabled}
      isOutfitPLPEnabled={isOutfitPLPEnabled}
      isHamburgerMenuRedesignEnabled={isHamburgerMenuRedesignEnabled}
      isGuestFavEnabled={isGuestFavEnabled}
      isGlobalHeaderRedesignEnabled={isGlobalHeaderRedesignEnabled}
      hideDrawerInfoSection={hideDrawerInfoSection}
      isMobileNavigationRedesignEnabled={isMobileNavigationRedesignEnabled}
      isBrandPartnerHoverEffectEnabled={isBrandPartnerHoverEffectEnabled}
    >
      <nav
        className={`${className} navigation nav-bar`}
        aria-label={isCondensedHeader && condensedNavigationAriaLbl}
      >
        <NavBar {...navBarProps} />
        {isGlobalDefaultHeaderRedesignEnabled && (
          <StoreAndHelpLinkWrapper>
            <button
              className="find-a-store-wrapper nav-drawer-link-wrapper"
              onClick={onStorePinClicked}
            >
              <FindAStoreIcon />
              <span className="nav-drawer-link-label">
                {getLabelFromCopyOrFromTranslation(
                  'global.header.lbl_header_storeDefaultTitle',
                  headerLabels
                )}
              </span>
            </button>
            <Anchor
              className="help-icon-wrapper nav-drawer-link-wrapper"
              to={internalEndpoints.helpCenterFAQ.link}
              asPath={internalEndpoints.helpCenterFAQ.path}
            >
              <HelpIcon />
              <span className="nav-drawer-link-label">
                {getLabelFromCopyOrFromTranslation(
                  `global.helpCenter.lbl_overview_help`,
                  HelpMenuLabels
                )}
              </span>
            </Anchor>
          </StoreAndHelpLinkWrapper>
        )}
      </nav>
    </Drawer>
  );
};

Navigation.propTypes = {
  openNavigationDrawer: PropTypes.bool,
  closeNavigationDrawer: PropTypes.func,
  nav: PropTypes.arrayOf(PropTypes.shape({})),
  className: PropTypes.string.isRequired,
  hideNavigationFooter: PropTypes.bool,
  showCondensedHeader: PropTypes.bool,
  userName: PropTypes.string,
  userPoints: PropTypes.string,
  userRewards: PropTypes.string,
  userNameClick: PropTypes.bool.isRequired,
  onLinkClick: PropTypes.func.isRequired,
  triggerLoginCreateAccount: PropTypes.bool.isRequired,
  openOverlay: PropTypes.func.isRequired,
  isCondensedHeader: PropTypes.bool,
  isShopBySizeAbTestOff: PropTypes.bool,
  onRouteChangeGlobalActions: PropTypes.func,
  isPlpPdpAnchoringEnabled: PropTypes.bool,
  isDisableBagAnchoring: PropTypes.bool,
  disableBabyL2Nesting: PropTypes.bool,
  isShowNavAnimation: PropTypes.bool,
  isNewNavigationImagesEnabled: PropTypes.bool,
  isGuestFavEnabled: PropTypes.bool,
  isBrandPartnerHoverEffectEnabled: PropTypes.bool,
};

Navigation.defaultProps = {
  isCondensedHeader: false,
  onRouteChangeGlobalActions: () => {},
  closeNavigationDrawer: () => {},
  isPlpPdpAnchoringEnabled: false,
  disableBabyL2Nesting: false,
  isDisableBagAnchoring: false,
  isShowNavAnimation: false,
  openNavigationDrawer: undefined,
  hideNavigationFooter: undefined,
  showCondensedHeader: undefined,
  userName: null,
  userPoints: null,
  userRewards: null,
  isShopBySizeAbTestOff: undefined,
  isNewNavigationImagesEnabled: false,
  isGuestFavEnabled: false,
  isBrandPartnerHoverEffectEnabled: false,
};

export { Navigation as NavigationVanilla };
export default withStyles(Navigation, style);
